import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import {
  Box,
  Button,
  Text,
  TextButton,
  TextButtonSize,
  TextButtonWeight,
} from '@wix/design-system';
import { useTranslation } from 'react-i18next';
import { ThemedButton } from '../ThemedComponents';
import { AppContextProvider } from '../AppLoader';

export interface ResendVerificationCodeProps {
  resendSuccessfullyIndication: boolean;
  resendThrottleTimerValue: number;
  sendConfirmationCode: () => void;
  resendErrorMessage?: string | null;
  resendButtonTranslationKey?: string;
  resendIndicationTranslationKey: string;
  resendButtonSize: TextButtonSize;
  resendButtonWeight?: TextButtonWeight;
  showAsButton?: boolean;
  variant?: 'themed';
}

export const ResendVerificationCode: React.FC<ResendVerificationCodeProps> =
  observer(
    ({
      resendSuccessfullyIndication,
      resendThrottleTimerValue,
      resendErrorMessage,
      sendConfirmationCode,
      resendButtonTranslationKey,
      resendIndicationTranslationKey,
      resendButtonSize,
      resendButtonWeight,
      showAsButton,
      variant,
    }) => {
      const { t } = useTranslation();
      const {
        rootStore: { displayStore },
      } = useContext(AppContextProvider);
      const isThemed = variant === 'themed';
      return (
        <Box
          direction="horizontal"
          gap={1}
          width={isThemed ? '100%' : undefined}
          align={isThemed ? 'center' : undefined}
        >
          {!resendSuccessfullyIndication && !showAsButton && (
            <TextButton
              size={resendButtonSize}
              weight={resendButtonWeight}
              onClick={sendConfirmationCode}
            >
              {t(
                resendButtonTranslationKey ??
                  'confirmEmailModalSecondStep.confirmationCode.resendLink.text',
              )}
            </TextButton>
          )}
          {!resendSuccessfullyIndication && showAsButton && isThemed && (
            <ThemedButton
              onClick={sendConfirmationCode}
              priority="secondary"
              size="large"
              fullWidth
              style={{
                ...(displayStore.preset.buttonDesign?.height && {
                  height: displayStore.buttonHeight,
                }),
                ...(displayStore.buttonFontSize && {
                  ['--wds-font-size-body-medium' as string]:
                    displayStore.buttonFontSize,
                }),
              }}
            >
              {t(
                resendButtonTranslationKey ??
                  'confirmEmailModalSecondStep.confirmationCode.resendLink.text',
              )}
            </ThemedButton>
          )}
          {!resendSuccessfullyIndication && showAsButton && !isThemed && (
            <Button
              onClick={sendConfirmationCode}
              priority="secondary"
              size="small"
            >
              {t(
                resendButtonTranslationKey ??
                  'confirmEmailModalSecondStep.confirmationCode.resendLink.text',
              )}
            </Button>
          )}
          {resendSuccessfullyIndication && (
            <Text size={resendButtonSize} weight="thin" light secondary>
              {t(resendIndicationTranslationKey, {
                time: resendThrottleTimerValue.toString(),
              })}
            </Text>
          )}
          {resendErrorMessage && (
            <Box inline marginInlineStart="6px">
              <Text skin="error" size="small">
                {resendErrorMessage}
              </Text>
            </Box>
          )}
        </Box>
      );
    },
  );
